import React, { useState, useMemo } from 'react';
import axios from 'axios';
import moment from 'moment';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Stack,
  Button,
  TextField,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  WarningAmberRounded as WarningIcon,
  MonitorHeart as VitalsIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { saveVitalsdb } from '../BaseURL';

const PRIMARY      = '#41a386';
const PRIMARY_DARK = '#2f8068';
const PRIMARY_BG   = 'rgba(65,163,134,0.10)';
const WARN         = '#b45309';
const WARN_BG      = 'rgba(234,179,8,0.10)';
const WARN_BORDER  = 'rgba(234,179,8,0.35)';
const DANGER       = '#b91c1c';
const DANGER_BG    = 'rgba(220,38,38,0.10)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';
const LABEL_SX     = {
  fontSize: '0.62rem',
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const MIN_PER_DAY = 3;

const EMPTY_DRAFT = {
  tem: '', pulse: '', res: '', pre: '', spo2: '', we: '', high: '',
};

const calcBMI = (we, high) => {
  const w = parseFloat(we);
  const h = parseFloat(high);
  if (!w || !h || h <= 0) return '';
  const m = h > 3 ? h / 100 : h; // accept cm or m
  const bmi = w / (m * m);
  if (!isFinite(bmi) || isNaN(bmi)) return '';
  return bmi.toFixed(1);
};

// Realistic ranges. Same numbers as the main Record New Vitals form so
// nurses see consistent behaviour whether they save for today or backfill.
const RANGES = {
  tem:   { min: 30,  max: 45,  unit: '°C',     label: 'Temperature' },
  pulse: { min: 40,  max: 200, unit: 'bpm',    label: 'Pulse' },
  res:   { min: 8,   max: 40,  unit: '/min',   label: 'Resp Rate' },
  spo2:  { min: 70,  max: 100, unit: '%',      label: 'SpO₂' },
  we:    { min: 0.1, max: 500, unit: 'kg',     label: 'Weight' },
  high:  { min: 30,  max: 250, unit: 'cm',     label: 'Height' },
};

// Returns { errors, firstMessage }. Each field is OPTIONAL except temperature.
// Empty optional fields don't trigger an error; only filled-in values are
// range-checked.
const validateDraft = (draft) => {
  const errors = {};
  const out    = (k, msg) => { errors[k] = msg; };

  // Temperature: required.
  const temStr = String(draft.tem || '').trim();
  if (!temStr) {
    out('tem', 'Required');
  } else {
    const t = Number(temStr);
    if (Number.isNaN(t)) out('tem', 'Must be a number');
    else if (t < RANGES.tem.min || t > RANGES.tem.max) {
      out('tem', `Must be ${RANGES.tem.min}–${RANGES.tem.max} °C`);
    }
  }

  // Numeric optionals.
  for (const k of ['pulse', 'res', 'spo2', 'we', 'high']) {
    const v = String(draft[k] || '').trim();
    if (!v) continue;
    const n = Number(v);
    const r = RANGES[k];
    if (Number.isNaN(n)) {
      out(k, 'Must be a number');
    } else if (n < r.min || n > r.max) {
      out(k, `Must be ${r.min}–${r.max} ${r.unit}`);
    }
  }

  // Blood pressure: optional, but if present must be sys/dia format with
  // realistic systolic / diastolic values.
  const pre = String(draft.pre || '').trim();
  if (pre) {
    const bpPattern = /^\d{2,3}\/\d{2,3}$/;
    if (!bpPattern.test(pre)) {
      out('pre', 'Use sys/dia (e.g. 120/80)');
    } else {
      const [sys, dia] = pre.split('/').map(Number);
      if (sys < 70 || sys > 250)      out('pre', 'Systolic must be 70–250');
      else if (dia < 40 || dia > 150) out('pre', 'Diastolic must be 40–150');
      else if (sys <= dia)            out('pre', 'Systolic must exceed diastolic');
    }
  }

  const firstMessage = errors.tem || errors.pulse || errors.res ||
                       errors.pre || errors.spo2 || errors.we || errors.high || '';

  return { errors, firstMessage, valid: Object.keys(errors).length === 0 };
};

// Vitals backfill timing rule. The first entry of a day is anchored at
// 06:00 (typical morning round). Each subsequent entry on the same date
// is stamped BACKFILL_GAP_HOURS after the latest existing entry. If that
// next slot crosses midnight we wrap into the early-morning hours so a
// late last-entry never blocks the nurse from adding more — the
// 3-per-day MIN_PER_DAY cap already prevents over-filling.
const BACKFILL_FIRST_HOUR = 6;
const BACKFILL_GAP_HOURS  = 4;

const toMinutes = (timeStr) => {
  const parts = String(timeStr || '').split(':').map(Number);
  if (parts.length < 2 || Number.isNaN(parts[0]) || Number.isNaN(parts[1])) return null;
  return parts[0] * 60 + parts[1];
};

const formatHHMMSS = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
};

// Returns { time, full } where `time` is the HH:mm:ss to stamp this
// backfill entry with. `full` is kept in the return shape for callers
// but is always false now — the per-day MIN_PER_DAY cap is what stops
// over-filling; the time itself just wraps past midnight when needed.
const computeNextBackfillTime = (date, vitalsHistory) => {
  const dayMins = (vitalsHistory || [])
    .filter(e => (e.dat_vist || e.dat) === date)
    .map(e => toMinutes(e.time))
    .filter(m => m != null)
    .sort((a, b) => a - b);

  if (dayMins.length === 0) {
    return { time: formatHHMMSS(BACKFILL_FIRST_HOUR * 60), full: false };
  }

  const lastMin = dayMins[dayMins.length - 1];
  const nextMin = (lastMin + BACKFILL_GAP_HOURS * 60) % (24 * 60);
  return { time: formatHHMMSS(nextMin), full: false };
};

const eachDateBetween = (startStr, endStr) => {
  if (!startStr) return [];
  const out = [];
  const start = new Date(`${startStr}T00:00:00`);
  const end   = new Date(`${(endStr || startStr)}T00:00:00`);
  if (isNaN(start) || isNaN(end)) return out;
  const cursor = new Date(start);
  while (cursor <= end) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, '0');
    const d = String(cursor.getDate()).padStart(2, '0');
    out.push(`${y}-${m}-${d}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
};

const todayYMD = () => moment().format('YYYY-MM-DD');

/**
 * Backfill table for past-day vitals. Sits below the existing Record New
 * Vitals card on the Patient Vitals tab so nurses can add missing entries
 * for any admission day — required after a doctor's discharge attempt
 * is blocked by the compliance check.
 *
 * Props:
 *   patient        { id, fullName, age, gender, insurance }
 *   admissionDate  YYYY-MM-DD
 *   currentUser    nurse username (for ownership)
 *   vitalsHistory  array of vitals records already loaded by the parent
 *                  (used to compute per-day counts without an extra fetch)
 *   onSaved        callback after a successful save (parent refetches)
 */
const ComplianceVitalsBackfill = ({
  patient,
  admissionDate,
  currentUser,
  vitalsHistory = [],
  onSaved,
}) => {
  const [expandedDate, setExpandedDate] = useState(null);
  const [showEntryFor, setShowEntryFor] = useState(null); // which date has its entry form open
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  // Group existing vitals by their dat_vist value.
  const byDate = useMemo(() => {
    const map = {};
    for (const v of vitalsHistory) {
      const d = v.dat_vist || v.dat;
      if (!d) continue;
      if (!map[d]) map[d] = [];
      map[d].push(v);
    }
    return map;
  }, [vitalsHistory]);

  // Reverse-chronological dates from admission → today (most recent first).
  const dates = useMemo(() => {
    const list = eachDateBetween(admissionDate, todayYMD());
    return list.reverse();
  }, [admissionDate]);

  if (!patient || !admissionDate || dates.length === 0) return null;

  const toggleExpand = (date) => {
    setExpandedDate(prev => (prev === date ? null : date));
    setShowEntryFor(null);
    setDraft(EMPTY_DRAFT);
  };

  const startEntry = (date) => {
    setShowEntryFor(date);
    setDraft(EMPTY_DRAFT);
  };

  const cancelEntry = () => {
    setShowEntryFor(null);
    setDraft(EMPTY_DRAFT);
  };

  const handleField = (k, v) => setDraft(prev => ({ ...prev, [k]: v }));

  const handleSave = async (date) => {
    const { valid, errors, firstMessage } = validateDraft(draft);
    if (!valid) {
      // Pick a field-specific message for the toast title. Temperature
      // is the most common one (required), so prefer that.
      const title = errors.tem ? 'Fix temperature value' : 'Fix the highlighted field';
      Swal.fire({ icon: 'warning', title, text: firstMessage || 'Some values are outside the realistic range.' });
      return;
    }
    // Auto-time the backfill entry so consecutive entries for the same
    // date are spaced at least 4 hours apart (anchored at 06:00 for the
    // first entry of the day).
    const { time: nextTime, full } = computeNextBackfillTime(date, vitalsHistory);
    if (full || !nextTime) {
      Swal.fire({
        icon: 'warning',
        title: 'No 4-hour slot left for this day',
        text: 'The latest entry on this date is too late in the day to fit another 4-hour-spaced vital. Pick a different date.',
      });
      return;
    }
    const bmi = calcBMI(draft.we, draft.high);
    setSaving(true);
    try {
      const res = await axios.post(saveVitalsdb, {
        patientId: patient.id,
        trans_code: null,
        tem:   draft.tem,
        pulse: draft.pulse || null,
        res:   draft.res   || null,
        pre:   draft.pre   || null,
        spo2:  draft.spo2  || null,
        we:    draft.we    || null,
        high:  draft.high  || null,
        bmi:   bmi || null,
        user:   currentUser,
        name:   patient.fullName,
        age:    patient.age,
        gender: patient.gender,
        status: patient.insurance,
        dat_vist: date,
        time: nextTime,
      }, { timeout: 15000 });

      if (res.data?.success) {
        await Swal.fire({
          icon: 'success',
          title: 'Vitals saved',
          text: `Recorded for ${date}.`,
          timer: 1400, showConfirmButton: false,
        });
        setDraft(EMPTY_DRAFT);
        setShowEntryFor(null);
        onSaved?.();
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: res.data?.message || 'Failed to save vitals' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message || 'Failed to save vitals' });
    } finally {
      setSaving(false);
    }
  };

  const incompleteCount = dates.reduce((acc, d) => {
    const c = (byDate[d] || []).length;
    return acc + (c < MIN_PER_DAY ? 1 : 0);
  }, 0);

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 2,
        borderColor: incompleteCount > 0 ? WARN_BORDER : BORDER,
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.75, py: 1.1,
          bgcolor: incompleteCount > 0 ? WARN_BG : PRIMARY_BG,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        {incompleteCount > 0
          ? <WarningIcon sx={{ fontSize: 18, color: WARN }} />
          : <CheckIcon sx={{ fontSize: 18, color: PRIMARY_DARK }} />}
        <Typography sx={{
          fontSize: '0.78rem', fontWeight: 700,
          color: incompleteCount > 0 ? WARN : PRIMARY_DARK,
          letterSpacing: '0.4px',
        }}>
          BACKFILL VITALS · {MIN_PER_DAY} REQUIRED PER DAY
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Chip
          size="small"
          label={incompleteCount > 0
            ? `${incompleteCount} day${incompleteCount === 1 ? '' : 's'} below minimum`
            : 'All days compliant'}
          sx={{
            height: 22, fontSize: '0.7rem', fontWeight: 700,
            bgcolor: '#fff',
            color: incompleteCount > 0 ? WARN : PRIMARY_DARK,
            border: `1px solid ${incompleteCount > 0 ? WARN_BORDER : 'rgba(65,163,134,0.3)'}`,
          }}
        />
      </Box>

      {/* Per-day rows */}
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {dates.map(date => {
          const entries = byDate[date] || [];
          const count = entries.length;
          const complete = count >= MIN_PER_DAY;
          const isExpanded = expandedDate === date;
          const isToday = date === todayYMD();
          const isEntryOpen = showEntryFor === date;

          return (
            <Paper
              key={date}
              variant="outlined"
              sx={{
                borderColor: complete ? BORDER : 'rgba(220,38,38,0.25)',
                bgcolor: '#fff',
                overflow: 'hidden',
              }}
            >
              {/* Row header */}
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1.25, py: 0.85,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#f8fafc' },
                }}
                onClick={() => toggleExpand(date)}
              >
                <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: STRONG, minWidth: 100 }}>
                  {date}
                  {isToday && (
                    <Box component="span" sx={{ ml: 0.75, fontSize: '0.65rem', fontWeight: 700, color: PRIMARY_DARK }}>
                      · TODAY
                    </Box>
                  )}
                </Typography>
                <Chip
                  size="small"
                  icon={<VitalsIcon sx={{ fontSize: 12 }} />}
                  label={`${count}/${MIN_PER_DAY}`}
                  sx={{
                    height: 22, fontSize: '0.72rem', fontWeight: 700,
                    bgcolor: complete ? PRIMARY_BG : DANGER_BG,
                    color: complete ? PRIMARY_DARK : DANGER,
                    border: `1px solid ${complete ? 'rgba(65,163,134,0.3)' : 'rgba(220,38,38,0.3)'}`,
                    '& .MuiChip-icon': { color: 'inherit', ml: '4px' },
                  }}
                />
                {!complete && (
                  <Typography sx={{ fontSize: '0.72rem', color: DANGER, fontStyle: 'italic' }}>
                    {MIN_PER_DAY - count} more required
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" sx={{ color: MUTED }}>
                  {isExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Box>

              <Collapse in={isExpanded} unmountOnExit>
                <Divider />
                <Box sx={{ px: 1.25, py: 1 }}>
                  {/* Existing entries */}
                  {entries.length > 0 && (
                    <Stack spacing={0.5} sx={{ mb: 1 }}>
                      <Typography sx={{ ...LABEL_SX, mb: 0.25 }}>Recorded entries</Typography>
                      {entries.map((e, idx) => (
                        <Box
                          key={e.trans_code || idx}
                          sx={{
                            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25,
                            px: 1, py: 0.6,
                            borderRadius: 0.75,
                            bgcolor: '#f8fafc',
                            border: `1px solid ${BORDER}`,
                          }}
                        >
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: STRONG, minWidth: 56 }}>
                            #{idx + 1}
                          </Typography>
                          {e.time && (
                            <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>{String(e.time).substring(0,5)}</Typography>
                          )}
                          {e.tem   && <FieldChip label="T"    value={`${e.tem}°C`} />}
                          {e.pulse && <FieldChip label="P"    value={`${e.pulse} bpm`} />}
                          {e.res   && <FieldChip label="R"    value={`${e.res}/min`} />}
                          {e.pre   && <FieldChip label="BP"   value={e.pre} />}
                          {e.spo2  && <FieldChip label="SpO₂" value={`${e.spo2}%`} />}
                          {e.we    && <FieldChip label="W"    value={`${e.we} kg`} />}
                          {e.high  && <FieldChip label="H"    value={`${e.high}`} />}
                          {e.bmi   && <FieldChip label="BMI"  value={e.bmi} />}
                          {e.user && (
                            <Typography sx={{ fontSize: '0.7rem', color: MUTED, ml: 'auto', fontStyle: 'italic' }}>
                              by {e.user}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  )}

                  {/* Compute the time slot this row's next backfill would
                      be stamped with so we can show "Next slot: 10:00" on
                      the trigger button and disable when the day is full. */}
                  {(() => {
                    const { time: nextTime, full } = computeNextBackfillTime(date, entries);
                    const buttonDisabled = complete || full;
                    const buttonLabel = complete
                      ? `Day already has ${MIN_PER_DAY}`
                      : full
                        ? 'Day full — no 4h slot left'
                        : `New entry for ${date}${nextTime ? ` · slot ${nextTime.substring(0, 5)}` : ''}`;
                    return !isEntryOpen ? (
                      <Button
                        size="small"
                        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                        onClick={() => startEntry(date)}
                        disabled={buttonDisabled}
                        sx={{
                          textTransform: 'none', fontSize: '0.78rem', fontWeight: 600,
                          px: 1.5, borderRadius: 1,
                          bgcolor: buttonDisabled ? '#f1f5f9' : PRIMARY,
                          color: buttonDisabled ? MUTED : '#fff',
                          '&:hover': {
                            bgcolor: buttonDisabled ? '#f1f5f9' : PRIMARY_DARK,
                          },
                          '&.Mui-disabled': { bgcolor: '#f1f5f9', color: MUTED },
                        }}
                      >
                        {buttonLabel}
                      </Button>
                    ) : (
                      <EntryForm
                        draft={draft}
                        onField={handleField}
                        onCancel={cancelEntry}
                        onSave={() => handleSave(date)}
                        saving={saving}
                        date={date}
                        nextTime={nextTime}
                        dayFull={full}
                      />
                    );
                  })()}
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
};

const FieldChip = ({ label, value }) => (
  <Box sx={{
    display: 'inline-flex', alignItems: 'baseline', gap: 0.5,
    px: 0.75, py: 0.15, borderRadius: 0.5,
    bgcolor: '#fff', border: `1px solid ${BORDER}`,
  }}>
    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.3px' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, color: STRONG }}>{value}</Typography>
  </Box>
);

const EntryForm = ({ draft, onField, onCancel, onSave, saving, date, nextTime, dayFull }) => {
  const bmiPreview = calcBMI(draft.we, draft.high);
  // Live validation: shows per-field helper text and disables Save when any
  // value is out of range so nurses can't submit nonsense like a 99 °C
  // temperature or a pulse of 9999 bpm.
  const { errors, valid } = validateDraft(draft);
  const niceTime = nextTime ? nextTime.substring(0, 5) : null;

  const fields = [
    { k: 'tem',   label: 'Temp (°C) *', placeholder: '36.5', required: true, hint: '30–45 °C' },
    { k: 'pulse', label: 'Pulse (bpm)',  placeholder: '80',     hint: '40–200 bpm' },
    { k: 'res',   label: 'Resp (/min)',  placeholder: '18',     hint: '8–40 /min' },
    { k: 'pre',   label: 'BP (mmHg)',    placeholder: '120/80', hint: 'sys/dia format' },
    { k: 'spo2',  label: 'SpO₂ (%)',     placeholder: '98',     hint: '70–100 %' },
    { k: 'we',    label: 'Weight (kg)',  placeholder: '70',     hint: 'up to 500 kg' },
    { k: 'high',  label: 'Height (cm)',  placeholder: '170',    hint: '30–250 cm' },
  ];

  return (
    <Box sx={{
      p: 1.25,
      borderRadius: 1,
      bgcolor: PRIMARY_BG,
      border: `1px solid rgba(65,163,134,0.3)`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ ...LABEL_SX, color: PRIMARY_DARK }}>
          NEW ENTRY · {date}
        </Typography>
        {niceTime && !dayFull && (
          <Chip
            size="small"
            label={`will be stamped ${niceTime}`}
            sx={{
              height: 20, fontSize: '0.66rem', fontWeight: 700,
              bgcolor: '#fff', color: PRIMARY_DARK,
              border: `1px solid rgba(65,163,134,0.35)`,
            }}
          />
        )}
        {dayFull && (
          <Chip
            size="small"
            label="day has no 4h slot left"
            sx={{
              height: 20, fontSize: '0.66rem', fontWeight: 700,
              bgcolor: '#fff', color: DANGER,
              border: `1px solid rgba(220,38,38,0.3)`,
            }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Cancel">
          <IconButton size="small" onClick={onCancel} disabled={saving} sx={{ color: MUTED }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(4, 1fr)',
          md: 'repeat(4, 1fr)',
        },
        gap: 1,
      }}>
        {fields.map(f => {
          const err = errors[f.k];
          return (
            <TextField
              key={f.k}
              size="small"
              label={f.label}
              placeholder={f.placeholder}
              value={draft[f.k]}
              onChange={(e) => onField(f.k, e.target.value)}
              required={!!f.required}
              disabled={saving}
              error={!!err}
              helperText={err || f.hint}
              InputLabelProps={{ shrink: true, sx: { fontSize: '0.72rem' } }}
              inputProps={{ inputMode: 'decimal' }}
              FormHelperTextProps={{ sx: { fontSize: '0.62rem', mx: 0.5 } }}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem', bgcolor: '#fff' } }}
            />
          );
        })}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          px: 1, borderRadius: 1, bgcolor: '#fff', border: `1px solid ${BORDER}`,
          // Match the helper-text vertical space of the TextFields so the
          // BMI tile sits flush with their input rows instead of below.
          alignSelf: 'flex-start', mt: '4px',
          height: '40px',
        }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', mr: 0.5 }}>BMI</Typography>
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: STRONG }}>{bmiPreview || '—'}</Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', gap: 0.75 }}>
        <Button
          size="small"
          onClick={onCancel}
          disabled={saving}
          sx={{ textTransform: 'none', fontSize: '0.78rem', color: MUTED }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
          onClick={onSave}
          disabled={saving || !valid || dayFull}
          sx={{
            textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
            bgcolor: PRIMARY, color: '#fff', boxShadow: 'none',
            '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#fff' },
          }}
        >
          {saving
            ? 'Saving…'
            : dayFull
              ? 'Day full'
              : `Save${niceTime ? ` @ ${niceTime}` : ''}`}
        </Button>
      </Box>
    </Box>
  );
};

export default ComplianceVitalsBackfill;
